import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  ButtonBase,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  MenuOpen,
  AccountCircle,
  Logout,
  Settings,
  Receipt,
  HelpOutline,
  ExpandMore,
  ChevronRight,
} from '@mui/icons-material';
import NotificationBell from '../NotificationBell';
import { useAuth } from '../../context/AuthContext';
import { relaunchWelcomeTour } from '../onboarding/WelcomeTour';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNavigation } from './useNavigation';
import { barIconButtonSx } from './barStyles';
import AppearanceMenu from './AppearanceMenu';

interface TopBarProps {
  drawerWidth: number;
  onMenuClick: () => void;
  onDesktopMenuClick: () => void;
  isSidebarCollapsed: boolean;
  topOffset?: number;
}

const TopBar: React.FC<TopBarProps> = ({
  drawerWidth,
  onMenuClick,
  onDesktopMenuClick,
  isSidebarCollapsed,
  topOffset = 0,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout, decodedToken } = useAuth();
  const { canRead } = useModulePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { activePage } = useNavigation();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const go = (path: string) => {
    navigate(path);
    handleClose();
  };

  const canAccessBilling = user?.isSystemAdmin === false && (
    decodedToken?.isSchoolAdmin === true || canRead('billing')
  );

  const roleLabel = user?.isSystemAdmin ? 'Super Admin' : user?.roleId?.name?.replace(/_/g, ' ');
  const initial = user?.name?.charAt(0).toUpperCase();
  const avatarGradient = `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.dark} 100%)`;

  const menuItems = [
    { label: 'Profile', icon: <AccountCircle />, onClick: () => go('/app/profile') },
    { label: 'Settings', icon: <Settings />, onClick: () => go('/app/settings') },
    ...(canAccessBilling ? [{ label: 'Billing', icon: <Receipt />, onClick: () => go('/app/billing') }] : []),
    { label: 'Relaunch tour', icon: <HelpOutline />, onClick: () => { handleClose(); relaunchWelcomeTour(); } },
  ];
  const menuItemSx = {
    py: 0.875,
    px: 1.5,
    mx: 0.75,
    borderRadius: '8px',
    '& .MuiListItemIcon-root': { minWidth: 30 },
    '& svg': { fontSize: 18 },
  };

  // Pages outside the sidebar (profile, settings, billing) are named from the URL.
  const getPageTitle = () => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    if (segments.length < 2) return 'Dashboard';
    
    const pageMap: Record<string, string> = {
      'dashboard': 'Dashboard',
      'schools': 'Schools',
      'demo-requests': 'Demo Requests',
      'subscriptions': 'Subscriptions',
      'users': 'Users',
      'admissions': 'Admissions',
      'administration': 'Administration',
      'accounts': 'Finance',
      'finance': 'Finance',
      'attendance': 'Attendance',
      'leaves': 'Leaves',
      'examination': 'Examination',
      'timetable': 'Timetable',
      'question-paper': 'Question Paper',
      'analysis': 'Analysis',
      'reports': 'Reports',
      'policies': 'Policies',
      'sops': 'SOPs',
      'settings': 'Settings',
      'profile': 'Profile',
      'billing': 'Billing',
    };
    
    return pageMap[segments[1]] || segments[1].charAt(0).toUpperCase() + segments[1].slice(1).replace(/-/g, ' ');
  };

  // Where you are: the sidebar group and section in quiet text, then the page.
  const crumbs = activePage ? activePage.crumbs : [];
  const pageTitle = activePage ? activePage.title : getPageTitle();

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { lg: `calc(100% - ${drawerWidth}px)` },
        ml: { lg: `${drawerWidth}px` },
        top: topOffset,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        borderRadius: 0,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.78 : 0.82),
        backdropFilter: 'saturate(180%) blur(14px)',
        borderBottom: 1,
        borderColor: 'divider',
        boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.3 : 0.03)}`,
        color: 'text.primary',
        transition: theme.transitions.create(['width', 'margin-left', 'top'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 56 }, px: { xs: 1.5, sm: 2.5 }, gap: 1 }}>
        {/* Mobile menu button */}
        <IconButton aria-label="open drawer" edge="start" onClick={onMenuClick} sx={[barIconButtonSx, { display: { lg: 'none' } }]}>
          <MenuIcon />
        </IconButton>

        {/* Desktop collapse button */}
        <Tooltip title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <IconButton
            aria-label="toggle sidebar"
            onClick={onDesktopMenuClick}
            sx={[barIconButtonSx, { display: { xs: 'none', lg: 'flex' }, ml: -0.75 }]}
          >
            <MenuOpen sx={{ transform: isSidebarCollapsed ? 'scaleX(-1)' : 'none', transition: 'transform 0.2s ease' }} />
          </IconButton>
        </Tooltip>

        {/* Breadcrumb */}
        <Box component="nav" aria-label="Breadcrumb" sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {crumbs.map((crumb) => (
            <React.Fragment key={crumb}>
              <Typography noWrap sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
                {crumb}
              </Typography>
              <ChevronRight sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 16, color: 'text.disabled' }} />
            </React.Fragment>
          ))}
          <Typography noWrap component="h1" sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>
            {pageTitle}
          </Typography>
        </Box>

        {/* Right side actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AppearanceMenu />

          {canRead('notifications') && <NotificationBell />}

          <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1.5 }} />

          {/* User Profile Button */}
          <ButtonBase
            onClick={handleMenu}
            data-tour="topbar-profile"
            aria-haspopup="menu"
            aria-expanded={Boolean(anchorEl)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              pl: 0.5,
              pr: { xs: 0.5, sm: 1 },
              py: 0.5,
              borderRadius: '999px',
              transition: theme.transitions.create('background-color', { duration: 150 }),
              bgcolor: anchorEl ? alpha(theme.palette.text.primary, 0.06) : 'transparent',
              '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06) },
              '&.Mui-focusVisible': { outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}` },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.875rem',
                fontWeight: 600,
                background: avatarGradient,
                color: 'common.white',
                boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 3.5px ${alpha(theme.palette.primary.main, 0.25)}`,
              }}
            >
              {initial}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left', maxWidth: 160 }}>
              <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.25 }}>
                {user?.name?.split(' ')[0]}
              </Typography>
              {roleLabel && (
                <Typography noWrap sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.25, textTransform: 'capitalize' }}>
                  {roleLabel}
                </Typography>
              )}
            </Box>
            <ExpandMore
              sx={{
                display: { xs: 'none', sm: 'block' },
                fontSize: 18,
                color: 'text.disabled',
                transform: anchorEl ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s ease',
              }}
            />
          </ButtonBase>

          {/* Profile Menu */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            sx={{
              mt: 1,
              '& .MuiPaper-root': {
                borderRadius: '14px',
                minWidth: 248,
                boxShadow: theme.palette.mode === 'dark'
                  ? `0 12px 32px ${alpha(theme.palette.common.black, 0.55)}`
                  : `0 12px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                border: 1,
                borderColor: 'divider',
              },
              '& .MuiList-root': { py: 0.75 },
            }}
          >
            {/* User Info Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, pt: 1, pb: 1.75 }}>
              <Avatar sx={{ width: 40, height: 40, fontSize: '1rem', fontWeight: 600, background: avatarGradient, color: 'common.white' }}>
                {initial}
              </Avatar>
              <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                  {user?.name}
                </Typography>
                <Typography
                  noWrap
                  sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3, fontFamily: user?.email ? 'inherit' : 'monospace' }}
                >
                  {user?.email || (user as any)?.userCode || '—'}
                </Typography>
                {roleLabel && (
                  <Typography
                    sx={{
                      mt: 0.5,
                      display: 'inline-block',
                      px: 0.875,
                      py: 0.125,
                      borderRadius: '6px',
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      bgcolor: alpha(user?.isSystemAdmin ? theme.palette.error.main : theme.palette.primary.main, 0.1),
                      color: user?.isSystemAdmin ? 'error.main' : 'primary.main',
                    }}
                  >
                    {roleLabel}
                  </Typography>
                )}
              </Box>
            </Box>
            <Divider sx={{ mx: 1.5, mb: 0.75 }} />

            {menuItems.map((item) => (
              <MenuItem key={item.label} onClick={item.onClick} sx={menuItemSx}>
                <ListItemIcon sx={{ color: 'text.secondary' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }} />
              </MenuItem>
            ))}

            <Divider sx={{ mx: 1.5, my: '6px !important' }} />

            <MenuItem
              onClick={() => { handleClose(); logout(); }}
              sx={{ ...menuItemSx, color: 'error.main', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) } }}
            >
              <ListItemIcon sx={{ color: 'error.main' }}><Logout /></ListItemIcon>
              <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 500 }} />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
